/**
 * The web page preview's state (htmlPreview.ts says what a web page is; the side panel's Preview tab, HtmlPreviewTab.tsx,
 * draws it): which pages the chat holds, the open page's text, and the Preview buttons' answers. Lives in the core, on every
 * surface that can show the side panel; does nothing while `enabled` is false. Whether the panel is open on Preview, and on
 * which page, is the side panel's (sidePanel.ts): the Preview tab's payload names the page (`{ sourceId }`).
 *
 * - A .txt file Gyro made is read once, through the same authenticated route as Download, to see whether it is a web page
 *   (Gyro offers web pages as .txt since v145). The text of one that is stays in memory (up to 2 MB) for its preview.
 * - Preview reads a page from what the chat already holds (an attachment, a code block, a file made on this computer) or
 *   from that route. It never sends the page to Gyro's server.
 * - A new web page arriving while the panel is open on Preview is shown at once ("When a new version arrives while the
 *   panel is open, switch to it"); older ones stay in the version list.
 * - Another chat, or another person, closes the panel while it shows a page of this one.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { track } from './telemetry.js';
import { PREVIEW_MAX_BYTES, artifactKey, artifactNeedsSniff, base64Bytes, decodePageBytes, isHtmlArtifact, looksLikeHtmlDocument, pageNameFromFile, previewSources, } from './htmlPreview.js';
/** A sniffed web page's text is kept for its preview up to this length. */
const KEEP_TEXT_CHARACTERS = 2 * 1024 * 1024;
export const PREVIEW_TOO_LARGE = 'This page is larger than 8 MB, too large to preview. Download it to open it in your browser.';
export const PREVIEW_FILE_GONE = 'This file is no longer available. Ask Gyro to make it again.';
export const PREVIEW_READ_FAILED = 'Gyro could not open this page. Try Refresh.';
/** The page a Preview tab payload names, or null. */
export function previewPayloadSource(payload) {
    const sourceId = payload?.sourceId;
    return typeof sourceId === 'string' && sourceId ? sourceId : null;
}
function readFailure(cause) {
    if (cause?.status === 404)
        return PREVIEW_FILE_GONE;
    if (cause instanceof Error && cause.message === PREVIEW_TOO_LARGE)
        return PREVIEW_TOO_LARGE;
    return PREVIEW_READ_FAILED;
}
export function useHtmlPreview({ enabled, messages, client, resolveLocal, conversationKey, open: panelOpen, selectedId, show, close }) {
    const [verdicts, setVerdicts] = useState(() => new Map());
    const texts = useRef(new Map());
    const [extra, setExtra] = useState([]);
    const [page, setPage] = useState({ status: 'loading' });
    const [renderKey, setRenderKey] = useState(0);
    const [readKey, setReadKey] = useState(0);
    const clientRef = useRef(client);
    clientRef.current = client;
    const resolveRef = useRef(resolveLocal);
    resolveRef.current = resolveLocal;
    const showRef = useRef(show);
    showRef.current = show;
    const closeRef = useRef(close);
    closeRef.current = close;
    const verdictOf = useCallback((artifact) => verdicts.get(artifactKey(artifact)) ?? 'unknown', [verdicts]);
    const sources = useMemo(() => (enabled ? previewSources(messages, verdictOf, extra) : []), [enabled, messages, verdictOf, extra]);
    const ids = useMemo(() => new Set(sources.map((source) => source.id)), [sources]);
    const selected = (enabled && panelOpen && sources.find((source) => source.id === selectedId)) || null;
    const readArtifact = useCallback(async (artifact, signal) => {
        const blob = artifact.source === 'desktop' ? resolveRef.current(artifact) : await clientRef.current.downloadArtifact(artifact, { signal });
        if (blob.size > PREVIEW_MAX_BYTES)
            throw new Error(PREVIEW_TOO_LARGE);
        return decodePageBytes(new Uint8Array(await blob.arrayBuffer()));
    }, []);
    // A .txt file Gyro made: read once to see whether it is a web page. Files of a reply still being written wait for it.
    const sniffable = useMemo(() => {
        if (!enabled)
            return [];
        const found = [];
        for (const message of messages) {
            if (message.role !== 'assistant' || message.pending || message.streaming || message.error)
                continue;
            for (const artifact of message.artifacts ?? [])
                if (artifactNeedsSniff(artifact))
                    found.push(artifact);
        }
        return found;
    }, [enabled, messages]);
    const sniffKeys = sniffable.map(artifactKey).join('|');
    const verdictsRef = useRef(verdicts);
    verdictsRef.current = verdicts;
    useEffect(() => {
        const queue = sniffable.filter((artifact) => !verdictsRef.current.has(artifactKey(artifact)));
        if (!queue.length)
            return undefined;
        const controller = new AbortController();
        void (async () => {
            for (const artifact of queue) {
                if (controller.signal.aborted)
                    return;
                const key = artifactKey(artifact);
                let verdict = 'not-web';
                try {
                    const text = await readArtifact(artifact, controller.signal);
                    if (looksLikeHtmlDocument(text)) {
                        verdict = 'web';
                        if (text.length <= KEEP_TEXT_CHARACTERS)
                            texts.current.set(key, text);
                    }
                }
                catch {
                    // An expired or unreadable file gets no Preview button; Download still says what is wrong with it.
                    if (controller.signal.aborted)
                        return;
                }
                setVerdicts((previous) => new Map(previous).set(key, verdict));
            }
        })();
        return () => controller.abort();
        // sniffKeys stands for sniffable; verdicts are read through their ref so a verdict never restarts the queue.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sniffKeys, readArtifact]);
    // The open page's text: from the chat, the kept text, or the artifact route. A new read for another page or a retry.
    useEffect(() => {
        if (!selected)
            return undefined;
        const source = selected;
        const controller = new AbortController();
        setPage({ status: 'loading' });
        const read = async () => {
            if (source.kind === 'code')
                return source.html ?? '';
            if (source.kind === 'attachment')
                return decodePageBytes(base64Bytes(source.attachment?.data ?? ''));
            const artifact = source.artifact;
            return texts.current.get(artifactKey(artifact)) ?? readArtifact(artifact, controller.signal);
        };
        read().then((html) => {
            if (controller.signal.aborted)
                return;
            if (html.length > PREVIEW_MAX_BYTES) {
                setPage({ status: 'error', message: PREVIEW_TOO_LARGE });
                return;
            }
            setPage({ status: 'ready', html });
            setRenderKey((value) => value + 1);
        }, (cause) => {
            if (!controller.signal.aborted)
                setPage({ status: 'error', message: readFailure(cause) });
        });
        return () => controller.abort();
        // The page is read again only for another page, a reopened panel or a retry: not for every new message.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selected?.id, readKey, readArtifact]);
    // A new web page while the panel is open on Preview: show it (the newest of those that arrived).
    const known = useRef(null);
    useEffect(() => {
        const previous = known.current;
        known.current = { key: conversationKey, ids };
        if (!previous || previous.key !== conversationKey || !panelOpen)
            return;
        const fresh = sources.find((source) => !previous.ids.has(source.id));
        if (fresh && (!selected || fresh.order > selected.order))
            showRef.current(fresh.id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ids, conversationKey]);
    // Another chat or another person: the panel closes if it shows a page of this one, and the files made on this computer
    // it listed are forgotten.
    const conversation = useRef(conversationKey);
    const panelOpenRef = useRef(panelOpen);
    panelOpenRef.current = panelOpen;
    useEffect(() => {
        if (conversation.current === conversationKey)
            return;
        conversation.current = conversationKey;
        if (panelOpenRef.current)
            closeRef.current();
        setExtra([]);
    }, [conversationKey]);
    const openSource = useCallback((id) => {
        track('gyro_preview_opened', { kind: 'source' });
        showRef.current(id);
    }, []);
    const openFile = useCallback((artifact, messageIndex) => {
        track('gyro_preview_opened', { kind: 'file' });
        const id = artifactKey(artifact);
        setExtra((previous) => (ids.has(id) || previous.some((source) => source.id === id) ? previous
            : [...previous, { id, kind: 'file', name: pageNameFromFile(artifact.name), order: messageIndex * 1000 + 900 + previous.length, artifact }]));
        showRef.current(id);
    }, [ids]);
    const isWebPage = useCallback((artifact) => enabled && (isHtmlArtifact(artifact) || (artifactNeedsSniff(artifact) && verdicts.get(artifactKey(artifact)) === 'web')), [enabled, verdicts]);
    const has = useCallback((id) => ids.has(id), [ids]);
    const refresh = useCallback(() => {
        if (page.status === 'error')
            setReadKey((value) => value + 1);
        else
            setRenderKey((value) => value + 1);
    }, [page.status]);
    return { enabled, sources, selected, page, renderKey, isWebPage, has, openFile, openSource, refresh };
}
//# sourceMappingURL=useHtmlPreview.js.map